import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Trash2, GripVertical, UserCheck, Check, XCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';

const FIELD_LABELS = {
  name: 'Name', title: 'Title', bio: 'Bio', website: 'Website', photo_url: 'Photo',
  email: 'Email', phone: 'Phone', facebook: 'Facebook', instagram: 'Instagram',
  linkedin: 'LinkedIn', twitter: 'X / Twitter', youtube: 'YouTube', tiktok: 'TikTok',
  spotify: 'Spotify',
};

const DETAIL_FIELDS = [
  ['phone', 'Phone'], ['email', 'Email'], ['website', 'Website'],
  ['facebook', 'Facebook'], ['instagram', 'Instagram'], ['linkedin', 'LinkedIn'],
  ['twitter', 'X / Twitter'], ['youtube', 'YouTube'], ['tiktok', 'TikTok'],
  ['spotify', 'Spotify'],
];

/** Everything the practitioner actually submitted, so approval isn't a guess. */
const PlacardDetails = ({ resource }) => {
  const rows = DETAIL_FIELDS.filter(([k]) => resource[k]);
  const links = [1, 2, 3]
    .map(n => [resource[`custom_link_${n}_label`], resource[`custom_link_${n}_url`]])
    .filter(([label, url]) => label || url);
  const isUrl = v => typeof v === 'string' && v.startsWith('http');

  if (!rows.length && !links.length) {
    return (
      <p className="text-xs text-gray-500 mt-2" data-testid={`placard-details-${resource.id}`}>
        No contact details, links or socials were submitted.
      </p>
    );
  }
  return (
    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs"
        data-testid={`placard-details-${resource.id}`}>
      {rows.map(([key, label]) => (
        <div key={key} className="flex gap-2 min-w-0">
          <dt className="text-gray-500 shrink-0">{label}:</dt>
          <dd className="text-gray-800 truncate">
            {isUrl(resource[key])
              ? <a href={resource[key]} target="_blank" rel="noopener noreferrer"
                   className="text-cyan-700 underline">{resource[key]}</a>
              : resource[key]}
          </dd>
        </div>
      ))}
      {links.map(([label, url], i) => (
        <div key={`cl${i}`} className="flex gap-2 min-w-0">
          <dt className="text-gray-500 shrink-0">{label || `Link ${i + 1}`}:</dt>
          <dd className="text-gray-800 truncate">
            {url
              ? <a href={url} target="_blank" rel="noopener noreferrer"
                   className="text-cyan-700 underline">{url}</a>
              : <span className="text-amber-700">no URL given</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
};

/**
 * Placards the platform built from a practitioner's own profile submission.
 * They stay invisible to members until approved here, then join the ordered
 * list below so they can be dragged into position like any other placard.
 */
const PlacardReviewQueue = ({ onPlacardsChanged, onEdit }) => {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/resources/pending`, { headers });
      setPending(r.data || []);
    } catch {
      setPending([]);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (resource, action) => {
    setBusy(resource.id);
    try {
      await axios.post(`${API}/resources/${resource.id}/${action}`, {}, { headers });
      toast.success(action === 'approve'
        ? `${resource.name}'s placard is live`
        : 'Dismissed');
      await load();
      onPlacardsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update placard');
    } finally {
      setBusy(null);
    }
  };

  if (!pending.length) return null;

  return (
    <Card className="border-amber-300 bg-amber-50/60" data-testid="placard-review-queue">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <UserCheck className="w-5 h-5" />
          Awaiting your approval ({pending.length})
        </CardTitle>
        <p className="text-sm text-amber-800/80">
          Built automatically from what each practitioner submitted. Members cannot see these until you
          approve them, and once approved they appear at the bottom of the list below for you to position.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {pending.map(r => {
          const changes = r.pending_update
            ? Object.keys(r.pending_update).filter(k => k !== 'submitted_at')
            : null;
          return (
            <div
              key={r.id}
              className="p-4 bg-white border border-amber-200 rounded-lg"
              data-testid={`pending-placard-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  {r.photo_url
                    ? <img src={r.photo_url} alt={r.name}
                           className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
                    : <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-5 h-5 text-amber-600" />
                      </div>}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold">{r.name}</h4>
                      <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        {changes ? 'Updated details' : 'New placard'}
                      </span>
                    </div>
                    <p className="text-sm text-cyan-700">{r.title}</p>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{r.bio}</p>
                    <PlacardDetails resource={r} />
                    {changes && (
                      <p className="text-xs text-amber-800 mt-2">
                        They changed: {changes.map(c => FIELD_LABELS[c] || c).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => act(r, 'approve')}
                    disabled={busy === r.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`approve-placard-${r.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    {changes ? 'Apply changes' : 'Approve'}
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => onEdit?.(r)}
                    disabled={busy === r.id}
                    data-testid={`edit-placard-${r.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" /> Edit first
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => act(r, 'dismiss')}
                    disabled={busy === r.id}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`dismiss-placard-${r.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Dismiss
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export const AdminGuidesTab = ({
  resources,
  resourceForm,
  setResourceForm,
  editingResource,
  resetResourceForm,
  saveResource,
  deleteResource,
  handleEditResource,
  handleDragStart,
  handleDragOver,
  handleDrop,
  draggedResource,
  resourceFormRef,
  onPlacardsChanged
}) => {
  const visible = (resources || []).filter(r => r.status !== 'pending');
  return (
    <>
          <PlacardReviewQueue
            onPlacardsChanged={onPlacardsChanged}
            onEdit={handleEditResource}
          />

          <Card ref={resourceFormRef}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingResource ? 'Edit Resource' : 'Create Resource/Practitioner'}</span>
                {editingResource && (
                  <Button variant="ghost" size="sm" onClick={resetResourceForm}>
                    Cancel Edit
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={resourceForm.name}
                    onChange={(e) => setResourceForm({ ...resourceForm, name: e.target.value })}
                    placeholder="Practitioner Name"
                  />
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={resourceForm.title}
                    onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                    placeholder="e.g., Life Coach, Meditation Expert"
                  />
                </div>
              </div>

              <div>
                <Label>Phone Number</Label>
                <Input
                  value={resourceForm.phone}
                  onChange={(e) => setResourceForm({ ...resourceForm, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  type="tel"
                />
              </div>
              
              <div>
                <Label>Bio *</Label>
                <Textarea
                  value={resourceForm.bio}
                  onChange={(e) => setResourceForm({ ...resourceForm, bio: e.target.value })}
                  placeholder="Biography (copy/paste from Word)"
                  rows={4}
                />
              </div>

              <div className="border-t pt-4">
                <Label className="text-lg font-semibold mb-3 block">Photo</Label>
                <div className="space-y-3">
                  <div>
                    <Label>Upload Photo (JPG, PNG, GIF, WebP)</Label>
                    <Input
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await axios.post(`${API}/upload-photo`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setResourceForm({ 
                              ...resourceForm, 
                              photo_url: response.data.file_url
                            });
                            toast.success('Photo uploaded successfully!');
                          } catch (error) {
                            toast.error('Photo upload failed');
                          }
                        }
                      }}
                      className="cursor-pointer"
                    />
                    {resourceForm.photo_url && !resourceForm.photo_url.startsWith('http') && (
                      <p className="text-xs text-green-600 mt-1">✓ Photo uploaded</p>
                    )}
                  </div>
                  <div>
                    <Label>Or paste external photo URL</Label>
                    <Input
                      value={resourceForm.photo_url}
                      onChange={(e) => setResourceForm({ ...resourceForm, photo_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-lg font-semibold mb-3 block">Links</Label>
                <div className="space-y-3">
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={resourceForm.website}
                      onChange={(e) => setResourceForm({ ...resourceForm, website: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-lg font-semibold mb-3 block">Social Media & Contact</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Facebook</Label>
                    <Input
                      value={resourceForm.facebook}
                      onChange={(e) => setResourceForm({ ...resourceForm, facebook: e.target.value })}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <Label>Instagram</Label>
                    <Input
                      value={resourceForm.instagram}
                      onChange={(e) => setResourceForm({ ...resourceForm, instagram: e.target.value })}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <Label>LinkedIn</Label>
                    <Input
                      value={resourceForm.linkedin}
                      onChange={(e) => setResourceForm({ ...resourceForm, linkedin: e.target.value })}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <div>
                    <Label>Twitter/X</Label>
                    <Input
                      value={resourceForm.twitter}
                      onChange={(e) => setResourceForm({ ...resourceForm, twitter: e.target.value })}
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div>
                    <Label>YouTube</Label>
                    <Input
                      value={resourceForm.youtube}
                      onChange={(e) => setResourceForm({ ...resourceForm, youtube: e.target.value })}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                  <div>
                    <Label>TikTok</Label>
                    <Input
                      value={resourceForm.tiktok}
                      onChange={(e) => setResourceForm({ ...resourceForm, tiktok: e.target.value })}
                      placeholder="https://tiktok.com/@..."
                    />
                  </div>
                  <div>
                    <Label>Spotify</Label>
                    <Input
                      value={resourceForm.spotify}
                      onChange={(e) => setResourceForm({ ...resourceForm, spotify: e.target.value })}
                      placeholder="https://open.spotify.com/..."
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={resourceForm.email}
                      onChange={(e) => setResourceForm({ ...resourceForm, email: e.target.value })}
                      placeholder="email@example.com"
                      type="email"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-lg font-semibold mb-3 block">Custom Links (Optional)</Label>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Link 1 Label</Label>
                      <Input
                        value={resourceForm.custom_link_1_label}
                        onChange={(e) => setResourceForm({ ...resourceForm, custom_link_1_label: e.target.value })}
                        placeholder="e.g., Book a Session"
                      />
                    </div>
                    <div>
                      <Label>Link 1 URL</Label>
                      <Input
                        value={resourceForm.custom_link_1_url}
                        onChange={(e) => setResourceForm({ ...resourceForm, custom_link_1_url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Link 2 Label</Label>
                      <Input
                        value={resourceForm.custom_link_2_label}
                        onChange={(e) => setResourceForm({ ...resourceForm, custom_link_2_label: e.target.value })}
                        placeholder="e.g., My Podcast"
                      />
                    </div>
                    <div>
                      <Label>Link 2 URL</Label>
                      <Input
                        value={resourceForm.custom_link_2_url}
                        onChange={(e) => setResourceForm({ ...resourceForm, custom_link_2_url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Link 3 Label</Label>
                      <Input
                        value={resourceForm.custom_link_3_label}
                        onChange={(e) => setResourceForm({ ...resourceForm, custom_link_3_label: e.target.value })}
                        placeholder="e.g., Free Resources"
                      />
                    </div>
                    <div>
                      <Label>Link 3 URL</Label>
                      <Input
                        value={resourceForm.custom_link_3_url}
                        onChange={(e) => setResourceForm({ ...resourceForm, custom_link_3_url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={saveResource} className="btn-primary w-full">
                {editingResource ? 'Update Resource' : 'Create Resource'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Existing Guides ({visible.length})</CardTitle>
              <p className="text-sm text-gray-500">Drag and drop to reorder</p>
            </CardHeader>
            <CardContent>
              {visible.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No resources created yet</p>
              ) : (
                <div className="space-y-3">
                  {visible.map((resource) => (
                    <div 
                      key={resource.id} 
                      className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-move transition-all ${
                        draggedResource?.id === resource.id ? 'opacity-50 scale-95' : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, resource)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, resource)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-gray-400 hover:text-gray-600">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{resource.name}</h3>
                          <p className="text-sm text-cyan-600">{resource.title}</p>
                          <p className="text-sm text-gray-600 mt-1">{resource.bio}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditResource(resource)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Resource?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete "{resource.name}".
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteResource(resource.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
    </>
  );
};

export default AdminGuidesTab;
